import { useEffect, useState } from 'react';
import Disclaimer from '@components/common/Disclaimer';
import { Dropdown } from '@components/common/Dropdown';
import DatePicker from '@components/common/NativeDatePicker';
import TableController, { TableCol, TableRow } from '@components/common/TableController';
import CommonWrapper, { commonWrapperOptions } from '@components/modal/CommonWrapper';
import TicketTimeEditV2 from '@components/modal/ticket/TicketTimeEditV2';
import { ExampleCustomInput } from '@components/modal/TicketEnrollModal';
import { postPayLog, userTicketUpdate } from '@lib/api';
import {
  getAdminUserTicketById,
  postAdminPayLog,
  putAdminUserTicketUpdate,
} from '@lib/api/v2/admin';
import { getTicketById } from '@lib/api/v2/ticket';
import dayjs from '@lib/dayjs';
import useHandleAPICall from '@lib/hooks/useHandleAPICall';
import useHandleError from '@lib/hooks/useHandleError';
import useModal from '@lib/hooks/useModal';
import { Button } from '@pages/merchant/seat/lockerChange';
import theme from '@theme/index';
import Format from '@util/formatter';
import { ko } from 'date-fns/locale';
import styled from 'styled-components';
import Swal from 'sweetalert2';

const ModalBottom = styled.div<{ $hasTimeButton: boolean }>`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(
    ${({ $hasTimeButton }) => ($hasTimeButton ? 3 : 2)},
    minmax(0, 1fr)
  );
  gap: 10px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

interface TicketInfoV2Props {
  ticketId: number;
  refetch?: () => void;
  account: AccountLevel;
}

const TicketInfoV2 = ({ ticketId, account, refetch }: TicketInfoV2Props) => {
  const { closeLatestModal, closeAllModal, openModal } = useModal();
  const { createRequest } = useHandleAPICall();
  const { catchError } = useHandleError();
  const [ticket, setTicket] = useState<TicketEdit | null>(null);
  const [loading, setLoading] = useState(true);
  const [validStart, setValidStart] = useState<Date>(new Date()); // 유효기간 시작일 (이용권 정보모달)
  const [validEnd, setValidEnd] = useState<Date>(new Date()); // 유효기간 종료일 (이용권 정보모달)
  const [level, setLevel] = useState<number>(1); // 이용권 정보 모달 등급
  const [addTime, setAddTime] = useState<number>(0); // 추가 시간 -> 일회권의 경우
  const [addDay, setAddDay] = useState<number>(0); // 추가 일자 -> 기간권의 경우
  const [addTimeCheck, setAddTimeCheck] = useState(false); // 시간/기간 수정 시 해당 정보 표시 여부
  const [amount, setAmount] = useState(0); // 금액
  const [payType, setPayType] = useState<string>('cash'); // 결제 종류
  const [sign, setSign] = useState('+'); // 적립/차감 여부

  useEffect(() => {
    const fetchTicket = async () => {
      try {
        setLoading(true);
        const getTicketFn = account === 'admin' ? getAdminUserTicketById : getTicketById;
        const { data } = await createRequest(getTicketFn, ticketId);

        // remainTime2 계산 추가
        const ticketWithRemainTime2 = {
          ...data,
          remainTime2: Format.calculateTicketRemainTime(data),
        };

        setTicket(ticketWithRemainTime2);
        setValidStart(dayjs(data.startDate).toDate());
        setValidEnd(dayjs(data.endDate).toDate());
        setLevel(data.level);
      } catch (error) {
        catchError(error);
        closeLatestModal();
      } finally {
        setLoading(false);
      }
    };

    fetchTicket();
  }, [ticketId]);

  const getTicketUpdateFn = () => {
    if (account === 'admin') return putAdminUserTicketUpdate;
    return userTicketUpdate;
  };

  const updateTicketFn = () => {
    if (account === 'admin') return postAdminPayLog;
    return postPayLog;
  };

  const onSaveEnded = () => {
    refetch && refetch();
    closeAllModal();
  };

  const handleOpenTimeModal = () =>
    openModal(
      () => (
        <TicketTimeEditV2
          data={ticket}
          check={setAddTimeCheck}
          time={setAddTime}
          setActualDay={setAddDay}
          payType={setPayType}
          setActualAmount={setAmount}
          signData={setSign}
        />
      ),
      commonWrapperOptions,
    );

  const onSaveModal = () => {
    const modifiedTicket: TicketEdit = {
      ...ticket,
      startDate: Format.formatDateTime(validStart.toISOString()),
      endDate: Format.formatDateTime(validEnd.toISOString()),
      level,
    };
    handleSave(modifiedTicket);
  };

  const handleSave = async (ticket: TicketEdit) => {
    // onSave

    if (addTime || addDay) {
      const maxTime = Math.ceil(
        dayjs.duration(dayjs(ticket.endDate).diff(new Date())).asSeconds() + 30,
      );

      const h = Math.floor(maxTime / 3600);
      const m = Math.floor((maxTime % 3600) / 60) - 1;

      if (sign === '-' && addTime > maxTime) {
        Swal.fire({
          icon: 'error',
          title: '남은시간보다 차감시간이 클 수 없습니다.',
          text: `${h > 0 ? (m > 0 ? `${h}시간 ${m}분` : `${h}시간`) : `${m}분`} 차감 가능합니다.`,
          timer: 3500,
        });
      } else {
        Swal.fire({
          title: '변경한 내용을 저장 하시겠습니까?',
          showCancelButton: true,
          confirmButtonColor: '#F53C14',
          cancelButtonColor: '#04c9db',
          confirmButtonText: '저장',
          cancelButtonText: '취소',
        }).then(async (result) => {
          if (result.value) {
            try {
              const res = await createRequest(getTicketUpdateFn(), {
                ticketId: ticket.id,
                merchantId: ticket.merchantId,
                userId: ticket.userId,
                startDate: ticket.startDate,
                endDate: ticket.endDate,
                time:
                  ticket.type === 2 || ticket.type === 5
                    ? 0
                    : (ticket.type as TicketType) !== 2 &&
                        (ticket.type as TicketType) !== 5 &&
                        sign === '-'
                      ? -addTime
                      : addTime,
                day: ticket.type === 2 || ticket.type === 5 ? addDay : 0,
                paymentType: payType,
                amount: amount,
                level: ticket.level,
              });

              if ((addTime || addDay) && res.status === 200) {
                const payData = {
                  paymentType: payType,
                  amount: amount,
                  userId: ticket.userId,
                  addTime: addTime,
                  addDay: addDay,
                  meta8: ` 시간:${
                    ticket.type === 2 || ticket.type === 5
                      ? 0
                      : (ticket.type as TicketType) !== 2 &&
                          (ticket.type as TicketType) !== 5 &&
                          sign === '-'
                        ? -addTime
                        : addTime
                  }초 기간:${ticket.type === 2 || ticket.type === 5 ? addDay : 0}일 ${
                    sign === '-' ? '차감' : '추가'
                  }`,
                  merchantId: ticket.merchantId,
                  ticketId: ticket.id,
                };
                await createRequest(updateTicketFn(), payData);
                Swal.fire('저장이 완료되었습니다.').then(onSaveEnded);
              }
            } catch (error) {
              catchError(error);
            }
          }
        });
      }
    } else {
      Swal.fire({
        title: '변경한 내용을 저장 하시겠습니까?',
        showCancelButton: true,
        confirmButtonColor: '#F53C14',
        cancelButtonColor: '#04c9db',
        confirmButtonText: '저장',
        cancelButtonText: '취소',
      }).then(async (result) => {
        if (result.value) {
          try {
            const res = await createRequest(getTicketUpdateFn(), {
              ticketId: ticket.id,
              merchantId: ticket.merchantId,
              userId: ticket.userId,
              startDate: ticket.startDate, // choiceStart ? choiceStart : e.startDate,
              endDate: ticket.endDate, // choiceEnd ? choiceEnd : e.endDate,
              time:
                ticket.type === 2 || ticket.type === 5
                  ? 0
                  : (ticket.type as TicketType) !== 2 &&
                      (ticket.type as TicketType) !== 5 &&
                      sign === '-'
                    ? -addTime
                    : addTime,
              day: ticket.type === 2 || ticket.type === 5 ? addDay : 0,
              paymentType: payType,
              amount: amount,
              level: ticket.level,
            });

            if (res.status === 200) {
              Swal.fire({
                title: '저장이 완료되었습니다.',
                timer: 2000,
              }).then(onSaveEnded);
            } else {
              Swal.fire('저장 중 에러가 발생했습니다.').then(onSaveEnded);
            }
          } catch (error) {
            catchError(error);
          }
        }
      });
    }
  };

  const getTicketTypeText = () => {
    switch (ticket.type) {
      case 1:
        return '일회권';
      case 2:
        return '기간권';
      case 3:
        return '시간정액권';
      case 4:
        return '세미나';
      case 5:
        return '사물함';
      default:
        return '-';
    }
  };

  const getUsedTimeText = () => {
    if (ticket.usedTime) return Format.formatSecondsToHHmmss(ticket.usedTime);
    if (ticket.isUsing === 2) return Format.formatSecondsToHHmmss(ticket.remainTime);
    if (!ticket.usedTime && ticket.type === 1) {
      const timeDiff = ticket.remainTime - ticket.remainTimeSec;
      if (timeDiff > 0) return Format.formatSecondsToHHmmss(timeDiff);
      if (timeDiff < 0) return Format.formatSecondsWithExcess(timeDiff);
    }
    return '00:00:00';
  };

  const getPaymentTypeText = () => {
    if (payType === 'cash') return '현금';
    if (payType === 'kakaopay') return '간편결제';
    if (payType === 'virtual') return '가매입/가매출';
    return '-';
  };

  if (loading || !ticket) {
    return (
      <CommonWrapper title="이용권 정보" isCancelButton={false} isConfirmButton={false}>
        <div style={{ padding: '2rem', textAlign: 'center' }}>로딩 중...</div>
      </CommonWrapper>
    );
  }

  return (
    <CommonWrapper
      title="이용권 정보"
      isCancelButton={false}
      isConfirmButton={false}
      bottomAddOn={
        <ModalBottom $hasTimeButton={ticket.type !== 4}>
          {ticket.type !== 4 && (
            <Button color="#186A3A" onClick={handleOpenTimeModal}>
              시간/기간 수정
            </Button>
          )}

          <Button color="#fff" onClick={closeLatestModal}>
            취소
          </Button>
          <Button color={theme.primary} onClick={onSaveModal}>
            저장
          </Button>
        </ModalBottom>
      }>
      <Content>
        {ticket?.seatType === 1 && (
          <Disclaimer
            type="warning"
            dense
            message={`고정석의 유효기간 시작일과 종료일을 강제 지정할 경우 이용권의 남은 시간도 자동으로 조정됩니다.
              종료일을 과거로 설정할 경우, 시간이 조정되면서 남은 시간이 모두 소진되어 강제 퇴장될 수 있으니 유의해 주세요.`}
          />
        )}
        <TableController>
          <TableRow isStart>
            <TableCol title="타입">{getTicketTypeText()}</TableCol>
          </TableRow>

          <TableRow>
            <TableCol title="구매자">{Format.formatPhone(ticket.phoneNum || '')}</TableCol>
          </TableRow>

          {(ticket.type === 1 || ticket.type === 2 || ticket.type === 3) && (
            <TableRow>
              <TableCol title="등급">
                <Dropdown>
                  <select value={level} onChange={(e) => setLevel(Number(e.target.value))}>
                    <option value={1}>1</option>
                    <option value={2}>2</option>
                    <option value={3}>3</option>
                    <option value={4}>4</option>
                  </select>
                </Dropdown>
              </TableCol>
            </TableRow>
          )}

          <TableRow>
            <TableCol title="시작일">{Format.formatDateTime(ticket.startDate)}</TableCol>
          </TableRow>

          <TableRow>
            <TableCol title="만료일">{Format.formatDateTime(ticket.endDate)}</TableCol>
          </TableRow>

          <TableRow>
            <TableCol title="마지막 입장">{Format.formatDateTime(ticket.lastIn || '')}</TableCol>
          </TableRow>

          <TableRow>
            <TableCol title="마지막 퇴장">
              {ticket.lastOut && ticket.lastOut !== ticket.lastIn
                ? Format.formatDateTime(ticket.lastOut)
                : '-'}
            </TableCol>
          </TableRow>

          <TableRow>
            <TableCol title="사용 시간">{getUsedTimeText()}</TableCol>
          </TableRow>

          <TableRow>
            <TableCol title="남은 시간">{ticket.remainTime2 || '-'}</TableCol>
          </TableRow>

          {ticket.type !== 4 && (
            <>
              <TableRow>
                <TableCol title={'유효기간 시작일\n강제 지정'}>
                  <DatePicker
                    selected={validStart}
                    onChange={(date: Date | null) => {
                      if (date) setValidStart(date);
                    }}
                    selectsStart
                    locale={ko}
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    yearDropdownItemNumber={20}
                    startDate={validStart}
                    maxDate={validEnd}
                    endDate={validEnd}
                    customInput={<ExampleCustomInput />}
                  />
                </TableCol>
              </TableRow>

              <TableRow>
                <TableCol title={'유효기간 만료일\n강제 지정'}>
                  <DatePicker
                    selected={validEnd}
                    onChange={(date: Date | null) => {
                      if (date) setValidEnd(date);
                    }}
                    selectsEnd
                    locale={ko}
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    yearDropdownItemNumber={20}
                    startDate={validStart}
                    minDate={validStart}
                    endDate={validEnd}
                    customInput={<ExampleCustomInput />}
                  />
                </TableCol>
              </TableRow>
            </>
          )}

          {addTimeCheck && (
            <>
              {ticket.type === 1 || ticket.type === 3 ? (
                <TableRow>
                  <TableCol title="추가시간">
                    {addTime && sign === '-'
                      ? `-${Math.floor(+addTime / 3600)}시간 ${(+addTime / 60) % 60}분`
                      : `${Math.floor(+addTime / 3600)}시간 ${(+addTime / 60) % 60}분`}
                  </TableCol>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCol title="추가일자">{addDay}일</TableCol>
                </TableRow>
              )}

              <TableRow>
                <TableCol title="금액">{amount}원</TableCol>
              </TableRow>

              <TableRow>
                <TableCol title="결제 종류">{getPaymentTypeText()}</TableCol>
              </TableRow>
            </>
          )}
        </TableController>

      </Content>
    </CommonWrapper>
  );
};

export default TicketInfoV2;
